/**
 * The states a screen is in when it has nothing to show — roadmap 4.2.
 *
 * These are components rather than each screen's own markup because they are
 * where a product feels unfinished: a list that renders nothing when empty, a
 * failure that renders nothing at all, and a load that shifts the layout when it
 * arrives. Getting them right once means sixty screens get them.
 *
 * Every label is a prop. Nothing here contains text, so nothing here needs
 * translating (§12.1).
 */
import { AlertTriangle, Inbox, Loader2 } from 'lucide-react'

import { Button } from './Button'
import { cn } from './cn'

// ---------------------------------------------------------------------------
export type SkeletonProps = React.ComponentPropsWithoutRef<'div'>

/**
 * A placeholder with the shape of the content that replaces it.
 *
 * `aria-hidden`: a screen reader announcing eight grey boxes is worse than
 * silence. The surrounding region carries `aria-busy` instead.
 */
export function Skeleton({ className, ...props }: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      className={cn('animate-pulse rounded-md bg-line', className)}
      {...props}
    />
  )
}

/** Rows of skeletons sized like table rows — the list loading state. */
export function SkeletonRows({ rows = 5, label }: { rows?: number; label: string }) {
  return (
    <div className="space-y-2" role="status" aria-busy="true" aria-label={label}>
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-14 w-full" />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
export function Spinner({ label, className }: { label: string; className?: string }) {
  return (
    <output aria-live="polite" className={cn('inline-flex items-center gap-2', className)}>
      <Loader2 aria-hidden="true" className="size-4 animate-spin" />
      <span className="text-sm text-fg-muted">{label}</span>
    </output>
  )
}

/** Centred spinner for a whole route or panel. */
export function LoadingPanel({ label }: { label: string }) {
  return (
    <div className="flex min-h-48 items-center justify-center p-8">
      <Spinner label={label} />
    </div>
  )
}

// ---------------------------------------------------------------------------
export type EmptyStateProps = {
  title: string
  body?: string | undefined
  /** The one thing to do next. An empty state without it is a dead end. */
  action?: React.ReactNode
  icon?: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>
  className?: string | undefined
}

export function EmptyState({
  title,
  body,
  action,
  icon: Icon = Inbox,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-xl border border-dashed border-line',
        'px-6 py-14 text-center',
        className,
      )}
    >
      <Icon aria-hidden="true" className="size-8 text-fg-subtle" />
      <h2 className="mt-4 text-base font-semibold">{title}</h2>
      {body ? <p className="mt-1 max-w-prose text-sm text-fg-muted">{body}</p> : null}
      {action ? <div className="mt-6">{action}</div> : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
export type ErrorStateProps = {
  title: string
  body?: string | undefined
  retryLabel?: string | undefined
  onRetry?: (() => void) | undefined
  className?: string | undefined
}

/**
 * A failure the user can act on.
 *
 * `role="alert"` so it is announced: a sighted user sees the panel change, and
 * without this a screen-reader user gets nothing at all.
 */
export function ErrorState({ title, body, retryLabel, onRetry, className }: ErrorStateProps) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center rounded-xl border border-danger/30',
        'bg-danger-soft px-6 py-12 text-center',
        className,
      )}
    >
      <AlertTriangle aria-hidden="true" className="size-8 text-danger" />
      <h2 className="mt-4 text-base font-semibold text-fg">{title}</h2>
      {body ? <p className="mt-1 max-w-prose text-sm text-fg-muted">{body}</p> : null}
      {onRetry && retryLabel ? (
        <Button variant="secondary" size="sm" className="mt-6" onClick={onRetry}>
          {retryLabel}
        </Button>
      ) : null}
    </div>
  )
}
